import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiGrid, FiImage, FiPlus, FiSearch, FiTrendingUp, FiX } from 'react-icons/fi';
import api, { STORAGE_URL } from '../../api/axios';
import { DashLang } from '../../utils/dashboardTranslations';

type SectionKey = 'top_selling' | 'sale';

interface Props {
  storeId: number;
  lang: DashLang;
  /** Current store_layouts values (may be partially filled). */
  layoutSettings: any;
  /** Called for every field change; the parent owns saving. */
  onChange: (key: string, value: any) => void;
}

const copy = {
  en: {
    top_selling: 'Top Selling Products',
    top_selling_hint: 'Shown as a highlighted row on your storefront home, in every theme.',
    sale: 'Sale & Offers',
    sale_hint: 'Shows products that have a discount — either every discounted product, or the ones you pick.',
    enable: 'Enable section',
    mode: 'Which products',
    auto_top: 'Automatic — most sold products',
    manual_top: 'Manual — I pick the products and their order',
    auto_sale: 'Automatic — all products with a discount',
    manual_sale: 'Manual — I pick the products and their order',
    limit: 'Maximum products to show',
    title_en: 'Section title (EN)',
    title_ar: 'Section title (AR)',
    selected: 'Selected products',
    noneSelected: 'No products picked yet. Search below to add them.',
    search: 'Search products by name or SKU…',
    add: 'Add',
    bestSellers: 'Most sold right now',
    sold: 'sold',
    noSales: 'No sales recorded yet — the automatic list will fill up after your first orders.',
    inactive: 'Inactive',
    noDiscount: 'No discount — will be hidden',
    autoNoteTop: 'Ranked automatically by how many units each product has sold (cancelled orders are ignored).',
    autoNoteSale: 'Every active product with a discount or a compare-at price is included automatically.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    loading: 'Loading products…',
    categories: 'Categories Showcase',
    categories_hint: 'Shows a card for every category with its own image on your storefront home.',
    cat_style: 'Card shape',
    cat_style_grid: 'Rounded cards — a grid that wraps',
    cat_style_circle: 'Circles — compact avatars',
    cat_style_carousel: 'Carousel — one row that scrolls sideways',
    cat_mode: 'Which categories',
    cat_limit: 'Maximum categories to show',
    cat_all: 'Automatic — every active category, in their sort order',
    cat_manual: 'Manual — I pick the categories and their order',
    cat_selected: 'Selected categories',
    cat_none: 'No categories picked yet. Add them below.',
    cat_search: 'Search categories…',
    cat_no_image: 'No image — add one from the Categories page',
    cat_empty: 'No categories yet. Create them from the Categories page first.',
    cat_title_en: 'Section title (EN)',
    cat_title_ar: 'Section title (AR)',
  },
  ar: {
    top_selling: 'المنتجات الأكثر مبيعاً',
    top_selling_hint: 'تظهر كصف مميز في صفحة متجرك الرئيسية، في جميع التصاميم.',
    sale: 'العروض والخصومات',
    sale_hint: 'تعرض المنتجات التي عليها خصم — إما كل المنتجات المخفضة أو التي تختارها.',
    enable: 'تشغيل القسم',
    mode: 'المنتجات المعروضة',
    auto_top: 'تلقائي — الأكثر مبيعاً',
    manual_top: 'يدوي — أنا أختار المنتجات وترتيبها',
    auto_sale: 'تلقائي — كل المنتجات التي عليها خصم',
    manual_sale: 'يدوي — أنا أختار المنتجات وترتيبها',
    limit: 'أقصى عدد للمنتجات المعروضة',
    title_en: 'عنوان القسم (إنجليزي)',
    title_ar: 'عنوان القسم (عربي)',
    selected: 'المنتجات المختارة',
    noneSelected: 'لم يتم اختيار منتجات بعد. ابحث بالأسفل لإضافتها.',
    search: 'ابحث عن منتج بالاسم أو الرمز…',
    add: 'إضافة',
    bestSellers: 'الأكثر مبيعاً حالياً',
    sold: 'مبيع',
    noSales: 'لا توجد مبيعات مسجلة بعد — ستمتلئ القائمة التلقائية بعد أول الطلبات.',
    inactive: 'غير مفعل',
    noDiscount: 'بدون خصم — لن يظهر',
    autoNoteTop: 'يتم الترتيب تلقائياً حسب عدد الوحدات المبيعة لكل منتج (الطلبات الملغاة لا تُحسب).',
    autoNoteSale: 'يتم تضمين كل منتج مفعل عليه خصم أو سعر مقارنة تلقائياً.',
    moveUp: 'تحريك للأعلى',
    moveDown: 'تحريك للأسفل',
    remove: 'إزالة',
    loading: 'جاري تحميل المنتجات…',
    categories: 'عرض الأقسام',
    categories_hint: 'يعرض بطاقة لكل قسم مع صورته الخاصة في الصفحة الرئيسية لمتجرك.',
    cat_style: 'شكل البطاقة',
    cat_style_grid: 'بطاقات بحواف دائرية — شبكة متعددة الأسطر',
    cat_style_circle: 'دوائر — صور مصغرة',
    cat_style_carousel: 'شريط — صف واحد يتحرك جانبياً',
    cat_mode: 'الأقسام المعروضة',
    cat_limit: 'أقصى عدد للأقسام المعروضة',
    cat_all: 'تلقائي — كل الأقسام المفعلة بترتيبها',
    cat_manual: 'يدوي — أنا أختار الأقسام وترتيبها',
    cat_selected: 'الأقسام المختارة',
    cat_none: 'لم يتم اختيار أقسام بعد. أضفها بالأسفل.',
    cat_search: 'ابحث عن قسم…',
    cat_no_image: 'بدون صورة — أضف صورة من صفحة الأقسام',
    cat_empty: 'لا توجد أقسام بعد. أنشئها من صفحة الأقسام أولاً.',
    cat_title_en: 'عنوان القسم (إنجليزي)',
    cat_title_ar: 'عنوان القسم (عربي)',
  },
};

export default function StorefrontSections({ storeId, lang, layoutSettings, onChange }: Props) {
  const t = copy[lang];
  const [products, setProducts] = useState<any[]>([]);
  const [bestSellers, setBestSellers] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [queries, setQueries] = useState<Record<SectionKey, string>>({ top_selling: '', sale: '' });
  const [catQuery, setCatQuery] = useState('');

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/stores/${storeId}/products`, { params: { per_page: 1000 } }).catch(() => ({ data: [] })),
      api.get(`/stores/${storeId}/best-sellers`, { params: { limit: 20 } }).catch(() => ({ data: [] })),
      api.get(`/stores/${storeId}/categories`).catch(() => ({ data: [] })),
    ]).then(([productRes, bestRes, catRes]) => {
      if (cancelled) return;
      const list = Array.isArray(productRes.data) ? productRes.data : (productRes.data?.data || []);
      setProducts(list);
      setBestSellers(Array.isArray(bestRes.data) ? bestRes.data : []);
      const cats = Array.isArray(catRes.data) ? catRes.data : (catRes.data?.data || []);
      setCategories(cats);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [storeId]);

  const productsById = useMemo(() => {
    const map: Record<number, any> = {};
    products.forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);

  const name = (product: any) => (lang === 'ar' && product?.name_ar ? product.name_ar : product?.name) || `#${product?.id}`;

  const idsFor = (section: SectionKey): number[] => {
    const raw = layoutSettings?.[`${section}_product_ids`];
    return Array.isArray(raw) ? raw.map(Number) : [];
  };

  const setIds = (section: SectionKey, ids: number[]) => onChange(`${section}_product_ids`, ids);

  const addProduct = (section: SectionKey, id: number) => {
    const ids = idsFor(section);
    if (ids.includes(id)) return;
    setIds(section, [...ids, id]);
  };

  const removeProduct = (section: SectionKey, id: number) => {
    setIds(section, idsFor(section).filter((x) => x !== id));
  };

  const move = (section: SectionKey, index: number, delta: number) => {
    const ids = [...idsFor(section)];
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setIds(section, ids);
  };

  const isOnSale = (product: any) => {
    if (!product) return false;
    if (product.discount_active || product.is_on_sale) return true;
    return product.compare_price != null && Number(product.compare_price) > Number(product.price);
  };

  const renderPicker = (section: SectionKey) => {
    const ids = idsFor(section);
    const query = queries[section].trim().toLowerCase();
    const matches = query
      ? products.filter((p) => !ids.includes(p.id) && [p.name, p.name_ar, p.sku].some((v: any) => String(v || '').toLowerCase().includes(query))).slice(0, 8)
      : [];

    return (
      <div style={{ marginTop: 16 }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--gray-700)' }}>
          {t.selected} {ids.length > 0 && <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>({ids.length})</span>}
        </h4>

        {ids.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 10 }}>{t.noneSelected}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {ids.map((id, i) => {
              const product = productsById[id];
              return (
                <div key={id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                  border: '1px solid var(--gray-100)', borderRadius: 8, background: 'white',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', minWidth: 16 }}>{i + 1}</span>
                  {product?.image ? (
                    <img src={`${STORAGE_URL}/${product.image}`} alt="" style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 30, height: 30, borderRadius: 6, background: 'var(--gray-50)', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {name(product) }
                    </div>
                    {product && product.is_active === false && (
                      <span style={{ fontSize: 10, color: 'var(--danger)' }}>{t.inactive}</span>
                    )}
                    {section === 'sale' && product && !isOnSale(product) && (
                      <span style={{ fontSize: 10, color: 'var(--warning, #d97706)' }}>{t.noDiscount}</span>
                    )}
                  </div>
                  <button type="button" title={t.moveUp} onClick={() => move(section, i, -1)} disabled={i === 0}
                    style={iconBtn(i === 0)}><FiArrowUp size={13} /></button>
                  <button type="button" title={t.moveDown} onClick={() => move(section, i, 1)} disabled={i === ids.length - 1}
                    style={iconBtn(i === ids.length - 1)}><FiArrowDown size={13} /></button>
                  <button type="button" title={t.remove} onClick={() => removeProduct(section, id)}
                    style={{ ...iconBtn(false), color: 'var(--danger)' }}><FiX size={14} /></button>
                </div>
              );
            })}
          </div>
        )}

        {/* Search & add */}
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: '6px 10px' }}>
            <FiSearch size={14} color="var(--gray-400)" />
            <input
              value={queries[section]}
              onChange={(e) => setQueries({ ...queries, [section]: e.target.value })}
              placeholder={t.search}
              style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, background: 'transparent', fontFamily: 'inherit' }}
            />
          </div>
          {matches.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
              {matches.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => { addProduct(section, product.id); setQueries({ ...queries, [section]: '' }); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer',
                    border: '1px solid var(--gray-100)', borderRadius: 8, background: 'var(--gray-50)', textAlign: 'start',
                  }}
                >
                  <FiPlus size={13} color="var(--primary)" />
                  <span style={{ fontSize: 13, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name(product)}</span>
                  {product.sku && <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{product.sku}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Best sellers as a shortcut for the top selling picker */}
        {section === 'top_selling' && (
          <div style={{ marginTop: 14 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiTrendingUp size={13} /> {t.bestSellers}
            </h4>
            {bestSellers.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>{t.noSales}</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {bestSellers.filter((p) => !ids.includes(p.id)).slice(0, 12).map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => addProduct('top_selling', product.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999,
                      border: '1px dashed var(--gray-200)', background: 'white', cursor: 'pointer', fontSize: 12,
                    }}
                  >
                    <FiPlus size={11} />
                    {name(product)}
                    <span style={{ color: 'var(--gray-400)' }}>· {product.sold_count} {t.sold}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (section: SectionKey) => {
    const enabled = !!layoutSettings?.[`show_${section}`];
    const mode = layoutSettings?.[`${section}_mode`] || 'auto';
    const limit = layoutSettings?.[`${section}_limit`] ?? 10;

    return (
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header"><h2>{t[section]}</h2></div>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14 }}>{t[`${section}_hint` as 'top_selling_hint']}</p>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => onChange(`show_${section}`, e.target.checked)} />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{t.enable}</span>
        </label>

        {enabled && (
          <>
            <div className="form-group">
              <label className="form-label">{t.mode}</label>
              {(['auto', 'manual'] as const).map((value) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="radio"
                    name={`${section}_mode`}
                    checked={mode === value}
                    onChange={() => onChange(`${section}_mode`, value)}
                  />
                  {section === 'top_selling'
                    ? (value === 'auto' ? t.auto_top : t.manual_top)
                    : (value === 'auto' ? t.auto_sale : t.manual_sale)}
                </label>
              ))}
              <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 6 }}>
                {mode === 'auto' ? (section === 'top_selling' ? t.autoNoteTop : t.autoNoteSale) : ''}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: '0 0 160px' }}>
                <label className="form-label">{t.limit}</label>
                <input
                  type="number" className="form-input" min={1} max={50}
                  value={limit}
                  onChange={(e) => onChange(`${section}_limit`, Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label className="form-label">{t.title_en}</label>
                <input
                  className="form-input"
                  placeholder={section === 'top_selling' ? 'Top Selling' : 'On Sale'}
                  value={layoutSettings?.[`${section}_title`] || ''}
                  onChange={(e) => onChange(`${section}_title`, e.target.value)}
                />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label className="form-label">{t.title_ar}</label>
                <input
                  className="form-input"
                  placeholder={section === 'top_selling' ? 'الأكثر مبيعاً' : 'عروض وخصومات'}
                  value={layoutSettings?.[`${section}_title_ar`] || ''}
                  onChange={(e) => onChange(`${section}_title_ar`, e.target.value)}
                />
              </div>
            </div>

            {mode === 'manual' && (loading
              ? <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>{t.loading}</p>
              : renderPicker(section))}
          </>
        )}
      </div>
    );
  };

  // ─── Categories showcase ───
  // An empty id list means "every active category"; a non-empty one is both
  // the selection and the display order.
  const categoryIds = (): number[] => {
    const raw = layoutSettings?.categories_section_ids;
    return Array.isArray(raw) ? raw.map(Number) : [];
  };

  const categoriesById = useMemo(() => {
    const map: Record<number, any> = {};
    categories.forEach((c) => { map[c.id] = c; });
    return map;
  }, [categories]);

  const catName = (cat: any) => (lang === 'ar' && cat?.name_ar ? cat.name_ar : cat?.name) || `#${cat?.id}`;

  const moveCategory = (index: number, delta: number) => {
    const ids = [...categoryIds()];
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    onChange('categories_section_ids', ids);
  };

  const renderCategoriesSection = () => {
    const enabled = !!layoutSettings?.show_categories_section;
    const style = layoutSettings?.categories_section_style || 'grid';
    const limit = layoutSettings?.categories_section_limit ?? 12;
    const ids = categoryIds();
    const manual = ids.length > 0;
    const query = catQuery.trim().toLowerCase();
    const matches = query
      ? categories.filter((c) => !ids.includes(c.id) && [c.name, c.name_ar].some((v: any) => String(v || '').toLowerCase().includes(query))).slice(0, 8)
      : [];

    return (
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header"><h2>{t.categories}</h2></div>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14 }}>{t.categories_hint}</p>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => onChange('show_categories_section', e.target.checked)} />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{t.enable}</span>
        </label>

        {enabled && (categories.length === 0 && !loading ? (
          <p style={{ fontSize: 13, color: 'var(--gray-400)' }}>{t.cat_empty}</p>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label">{t.cat_style}</label>
              {([
                ['grid', t.cat_style_grid],
                ['circle', t.cat_style_circle],
                ['carousel', t.cat_style_carousel],
              ] as const).map(([value, label]) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="radio"
                    name="categories_section_style"
                    checked={style === value}
                    onChange={() => onChange('categories_section_style', value)}
                  />
                  {label}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: '0 0 160px' }}>
                <label className="form-label">{t.cat_limit}</label>
                <input
                  type="number" className="form-input" min={1} max={50}
                  value={limit}
                  onChange={(e) => onChange('categories_section_limit', Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label className="form-label">{t.cat_title_en}</label>
                <input
                  className="form-input"
                  placeholder="Shop by Category"
                  value={layoutSettings?.categories_section_title || ''}
                  onChange={(e) => onChange('categories_section_title', e.target.value)}
                />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label className="form-label">{t.cat_title_ar}</label>
                <input
                  className="form-input"
                  placeholder="تسوقي حسب القسم"
                  value={layoutSettings?.categories_section_title_ar || ''}
                  onChange={(e) => onChange('categories_section_title_ar', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t.cat_mode}</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                <input type="radio" name="categories_section_mode" checked={!manual} onChange={() => onChange('categories_section_ids', [])} />
                {t.cat_all}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="radio"
                  name="categories_section_mode"
                  checked={manual}
                  onChange={() => onChange('categories_section_ids', categories.slice(0, limit).map((c) => c.id))}
                />
                {t.cat_manual}
              </label>
            </div>

            {/* Preview of what will show, in the chosen order */}
            <h4 style={{ fontSize: 13, fontWeight: 600, margin: '8px 0', color: 'var(--gray-700)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiGrid size={13} /> {manual ? t.cat_selected : t.categories}
            </h4>
            {manual ? (
              ids.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 10 }}>{t.cat_none}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {ids.map((id, i) => {
                    const cat = categoriesById[id];
                    return (
                      <div key={id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                        border: '1px solid var(--gray-100)', borderRadius: 8, background: 'white',
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', minWidth: 16 }}>{i + 1}</span>
                        {cat?.image ? (
                          <img src={`${STORAGE_URL}/${cat.image}`} alt="" style={{ width: 34, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 34, height: 40, borderRadius: 6, background: 'var(--gray-50)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiImage size={14} color="var(--gray-300)" />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{catName(cat)}</div>
                          {cat && !cat.image && <span style={{ fontSize: 10, color: 'var(--warning, #d97706)' }}>{t.cat_no_image}</span>}
                        </div>
                        <button type="button" title={t.moveUp} onClick={() => moveCategory(i, -1)} disabled={i === 0}
                          style={iconBtn(i === 0)}><FiArrowUp size={13} /></button>
                        <button type="button" title={t.moveDown} onClick={() => moveCategory(i, 1)} disabled={i === ids.length - 1}
                          style={iconBtn(i === ids.length - 1)}><FiArrowDown size={13} /></button>
                        <button type="button" title={t.remove} onClick={() => onChange('categories_section_ids', ids.filter((x) => x !== id))}
                          style={{ ...iconBtn(false), color: 'var(--danger)' }}><FiX size={14} /></button>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {categories.slice(0, limit).map((cat) => (
                  <div key={cat.id} style={{ width: 64, textAlign: 'center' }}>
                    {cat.image ? (
                      <img src={`${STORAGE_URL}/${cat.image}`} alt="" style={{ width: 64, height: 74, borderRadius: style === 'circle' ? '50%' : 10, objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: 64, height: 74, borderRadius: style === 'circle' ? '50%' : 10, background: 'var(--gray-50)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }} title={t.cat_no_image}>
                        <FiImage size={16} color="var(--gray-300)" />
                      </div>
                    )}
                    <div style={{ fontSize: 11, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{catName(cat)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Search & add (manual mode only) */}
            {manual && (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: '6px 10px' }}>
                  <FiSearch size={14} color="var(--gray-400)" />
                  <input
                    value={catQuery}
                    onChange={(e) => setCatQuery(e.target.value)}
                    placeholder={t.cat_search}
                    style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, background: 'transparent', fontFamily: 'inherit' }}
                  />
                </div>
                {matches.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                    {matches.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => { onChange('categories_section_ids', [...ids, cat.id]); setCatQuery(''); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer',
                          border: '1px solid var(--gray-100)', borderRadius: 8, background: 'var(--gray-50)', textAlign: 'start',
                        }}
                      >
                        <FiPlus size={13} color="var(--primary)" />
                        <span style={{ fontSize: 13, flex: 1 }}>{catName(cat)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ))}
      </div>
    );
  };

  return (
    <>
      {renderCategoriesSection()}
      {renderSection('top_selling')}
      {renderSection('sale')}
    </>
  );
}

const iconBtn = (disabled: boolean): React.CSSProperties => ({
  border: '1px solid var(--gray-100)',
  background: 'white',
  borderRadius: 6,
  padding: '3px 5px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.35 : 1,
  display: 'flex',
  alignItems: 'center',
  color: 'var(--gray-500)',
});
